// ─── DistanceCalculator, CalorieCalculator, StepService ────────────────────────
import type { GpsPoint } from "./types";
import { haversine } from "./LocationService";

export function calcGpsDistance(points: GpsPoint[]): number {
  let dist = 0;
  for (let i = 1; i < points.length; i++) {
    if (!points[i].moving) continue;
    dist += haversine(points[i - 1].lat, points[i - 1].lng, points[i].lat, points[i].lng);
  }
  return dist;
}

export function formatDistance(meters: number, unit: "km" | "mi"): string {
  if (unit === "mi") return (meters / 1609.344).toFixed(2);
  return (meters / 1000).toFixed(2);
}

export function metersToUnit(meters: number, unit: "km" | "mi"): number {
  return unit === "mi" ? meters / 1609.344 : meters / 1000;
}

export function speedToUnit(mps: number, unit: "km" | "mi"): number {
  if (unit === "mi") return mps * 2.236936;
  return mps * 3.6;
}

export function formatPace(secPerKm: number, unit: "km" | "mi"): string {
  if (unit === "mi") secPerKm = secPerKm * 1.609344;
  const m = Math.floor(secPerKm / 60);
  const s = Math.round(secPerKm % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function calcCalories(
  weightKg: number,
  _distanceM: number,
  movingSecs: number,
  elevationGainM: number,
): number {
  const hours = movingSecs / 3600;
  const met = 3.5;
  const baseCal = met * weightKg * hours;
  const inclineBonus = (elevationGainM * weightKg * 9.81) / 4184;
  return Math.round((baseCal + inclineBonus) * 100) / 100;
}

export function calcAvgPace(distanceM: number, movingSecs: number): number {
  if (distanceM < 1 || movingSecs < 1) return 0;
  return (movingSecs / (distanceM / 1000));
}

// ─── StepService — native plugin + accelerometer fallback ──────────────────────

import { Capacitor } from "@capacitor/core";
import type { PluginListenerHandle } from "@capacitor/core";
import { StepCounter } from "../plugins/stepCounter";

const STEP_THRESHOLD = 1.5;
const MIN_STEP_INTERVAL_MS = 250;
const GRAVITY_ALPHA = 0.8;
const IDLE_TIMEOUT_MS = 4000;

export class StepService {
  private steps = 0;
  private lastStepTs = 0;
  private lastAccel = 0;
  private moving = false;
  private idleTimer: ReturnType<typeof setTimeout> | null = null;
  private listener: ((steps: number) => void) | null = null;
  private boundHandler: ((e: DeviceMotionEvent) => void) | null = null;
  private nativeListener: PluginListenerHandle | null = null;
  private usingNative = false;

  async start(listener: (steps: number) => void): Promise<boolean> {
    this.listener = listener;
    this.steps = 0;
    this.lastStepTs = 0;
    this.moving = false;

    // Try native hardware step counter first (works with screen off)
    if (Capacitor.isNativePlatform()) {
      try {
        const avail = await StepCounter.isAvailable();
        if (avail.available) {
          if (!avail.hasPermission) {
            const perm = await StepCounter.requestPermission();
            if (!perm.granted) throw new Error("no permission");
          }
          await StepCounter.start();
          this.usingNative = true;
          this.nativeListener = await StepCounter.addListener("stepUpdate", (data) => {
            this.steps = data.steps;
            this.moving = true;
            this.lastStepTs = Date.now();
            if (this.idleTimer) clearTimeout(this.idleTimer);
            this.idleTimer = setTimeout(() => { this.moving = false; }, IDLE_TIMEOUT_MS);
            if (this.listener) this.listener(this.steps);
          });
          return true;
        }
      } catch {
        // Fall through to accelerometer
      }
    }

    // Fallback: browser accelerometer
    this.usingNative = false;
    this.boundHandler = this.onMotion.bind(this);
    window.addEventListener("devicemotion", this.boundHandler as EventListener);
    return true;
  }

  stop() {
    if (this.usingNative) {
      StepCounter.stop().catch(() => {});
      this.nativeListener?.remove().catch(() => {});
      this.nativeListener = null;
      this.usingNative = false;
    }
    if (this.boundHandler) window.removeEventListener("devicemotion", this.boundHandler as EventListener);
    if (this.idleTimer) clearTimeout(this.idleTimer);
    this.boundHandler = null;
    this.listener = null;
  }

  getSteps() { return this.steps; }
  isMoving() { return this.moving; }
  isUsingNative() { return this.usingNative; }

  private onMotion(e: DeviceMotionEvent) {
    const a = e.accelerationIncludingGravity;
    if (!a || a.x == null || a.y == null || a.z == null) return;
    const mag = Math.sqrt(a.x * a.x + a.y * a.y + a.z * a.z);
    this.lastAccel = GRAVITY_ALPHA * this.lastAccel + (1 - GRAVITY_ALPHA) * mag;
    const delta = Math.abs(mag - this.lastAccel);
    const now = Date.now();

    if (delta > STEP_THRESHOLD && now - this.lastStepTs > MIN_STEP_INTERVAL_MS) {
      this.steps++;
      this.lastStepTs = now;
      this.moving = true;
      if (this.idleTimer) clearTimeout(this.idleTimer);
      this.idleTimer = setTimeout(() => { this.moving = false; }, IDLE_TIMEOUT_MS);
      if (this.listener) this.listener(this.steps);
    }
  }
}

export function requestMotionPermission(): Promise<boolean> {
  return new Promise(async (resolve) => {
    try {
      const DME = window.DeviceMotionEvent as unknown as { requestPermission?: () => Promise<string> };
      if (typeof DME?.requestPermission === "function") {
        const result = await DME.requestPermission();
        resolve(result === "granted");
      } else {
        resolve(true);
      }
    } catch { resolve(false); }
  });
}
